import React from 'react';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import { FinalSubmissionsOverview } from 'components/FinalEvaluator';
import YearSelectorBar from 'components/YearSelectorBar';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { useFinalEvaluator } from 'hooks/useFinalEvaluator';

const FinalEvaluatorPage: React.FC = () => {
  const {
    isAuthenticated,
    userHasAccess,
    userIsPillarPIC,
    loading,
    year,
    setYear,
    EARLIEST_YEAR,
    currentYear,
    successMessage,
    isRefreshingJudgments,
    submissions,
    existingJudgments,
    handleCompleteAll,
    isCompletingAll,
    handleEvaluationSubmit,
    error,
    fetchFinalSubmissions,
    validUserCategories,
  } = useFinalEvaluator();

  // If not authenticated, show not authenticated message
  if (!isAuthenticated()) {
    return (
      <PageLayout
        title="Final Round Evaluations"
        subtitle="Authentication required to access final evaluations."
      >
        <NotAuthenticated
          message="Please log in to access the final evaluator. You need to be an authorized category judge to evaluate final submissions."
          buttonLink="/"
        />
      </PageLayout>
    );
  }

  // If user doesn't have access, show access denied message
  if (!userHasAccess) {
    return (
      <PageLayout
        title="Final Round Evaluations"
        subtitle="Access restricted to authorized category judges."
      >
        <AccessDenied
          message={
            userIsPillarPIC
              ? 'Pillar PICs do not have access to final evaluations. Only category judges and award admins can evaluate final submissions.'
              : "You don't have permission to evaluate final submissions. Access is restricted to authorized category judges and award admins only."
          }
        />
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout
        title="Final Round Evaluations"
        subtitle="Evaluating live pitch presentations"
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingState message="Loading final submissions..." />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Final Round Evaluations"
      subtitle="In the arena of excellence, may the finest effort claim victory"
    >
      <YearSelectorBar
        year={year}
        setYear={setYear}
        earliestYear={EARLIEST_YEAR}
        currentYear={currentYear}
      />

      {/* Success Message Display */}
      {successMessage && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <p className="text-green-800 font-baijamjuree font-semibold text-sm">
              {successMessage}
            </p>
            {isRefreshingJudgments && (
              <div className="flex items-center gap-2 text-green-600">
                <div className="w-3 h-3 border border-green-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-baijamjuree">Updating...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Single Page Scoring */}
      <FinalSubmissionsOverview
        submissions={submissions}
        existingJudgments={existingJudgments}
        onCompleteAll={handleCompleteAll}
        isCompletingAll={isCompletingAll}
        onEvaluationSubmit={handleEvaluationSubmit}
        error={error ?? undefined}
        onRetry={() => fetchFinalSubmissions(year)}
        validUserCategories={validUserCategories}
        year={year}
        isReadOnly={year !== currentYear}
      />
    </PageLayout>
  );
};

export default FinalEvaluatorPage;
